import type { Object3D } from 'three';
import {
  AssemblyReferenceCopyKind,
  type AssemblyReferenceCopyMetadata,
} from './AssemblyReferenceCopyTypes';

/** Object3D.metadata 中保存装配体引用复制元数据的键。 */
const ASSEMBLY_REFERENCE_COPY_METADATA_KEY = 'assemblyReferenceCopy';

/**
 * 判断未知值是否为普通记录。
 *
 * @param value 待判断值。
 * @returns 是否为普通记录。
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

/**
 * 读取非空字符串。
 *
 * @param value 待读取值。
 * @returns 非空字符串；无效时返回空字符串。
 */
function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * 克隆引用复制元数据，避免外部持有可变引用。
 *
 * @param metadata 源元数据。
 * @returns 克隆后的元数据。
 */
function cloneReferenceCopyMetadata(
  metadata: AssemblyReferenceCopyMetadata,
): AssemblyReferenceCopyMetadata {
  return {
    kind: metadata.kind,
    sourceNodeId: metadata.sourceNodeId,
    ...(metadata.sourcePartFileUuid ? { sourcePartFileUuid: metadata.sourcePartFileUuid } : {}),
    ...(metadata.sourceComponentInstanceId ? { sourceComponentInstanceId: metadata.sourceComponentInstanceId } : {}),
    ...(metadata.parentNodeId ? { parentNodeId: metadata.parentNodeId } : {}),
    //------ 装配体引用复制族 开始------
    ...(metadata.referenceFamilyId ? { referenceFamilyId: metadata.referenceFamilyId } : {}),
    ...(metadata.definitionId ? { definitionId: metadata.definitionId } : {}),
    ...(metadata.originOccurrenceId ? { originOccurrenceId: metadata.originOccurrenceId } : {}),
    ...(metadata.copiedFromOccurrenceId ? { copiedFromOccurrenceId: metadata.copiedFromOccurrenceId } : {}),
    //------ 装配体引用复制族 结束------
  };
}

/**
 * 从普通记录中解析引用复制元数据。
 *
 * @param value 待解析值。
 * @returns 有效元数据；无效时返回 null。
 */
function parseReferenceCopyMetadata(value: unknown): AssemblyReferenceCopyMetadata | null {
  if (!isRecord(value) || value.kind !== AssemblyReferenceCopyKind.ReferenceOccurrence) {
    return null;
  }

  const sourceNodeId = readString(value.sourceNodeId);
  if (!sourceNodeId) {
    return null;
  }

  const sourcePartFileUuid = readString(value.sourcePartFileUuid);
  const sourceComponentInstanceId = readString(value.sourceComponentInstanceId);
  const parentNodeId = readString(value.parentNodeId);
  //------ 装配体引用复制族 开始------
  const referenceFamilyId = readString(value.referenceFamilyId);
  const definitionId = readString(value.definitionId);
  const originOccurrenceId = readString(value.originOccurrenceId);
  const copiedFromOccurrenceId = readString(value.copiedFromOccurrenceId);
  //------ 装配体引用复制族 结束------

  return {
    kind: AssemblyReferenceCopyKind.ReferenceOccurrence,
    sourceNodeId,
    ...(sourcePartFileUuid ? { sourcePartFileUuid } : {}),
    ...(sourceComponentInstanceId ? { sourceComponentInstanceId } : {}),
    ...(parentNodeId ? { parentNodeId } : {}),
    //------ 装配体引用复制族 开始------
    ...(referenceFamilyId ? { referenceFamilyId } : {}),
    ...(definitionId ? { definitionId } : {}),
    ...(originOccurrenceId ? { originOccurrenceId } : {}),
    ...(copiedFromOccurrenceId ? { copiedFromOccurrenceId } : {}),
    //------ 装配体引用复制族 结束------
  };
}

/**
 * 装配体引用复制元数据服务。
 *
 * 设计说明：
 * 1. 引用复制是装配体主状态，不写入 userData，统一使用 Object3D.metadata；
 * 2. 服务只负责读写可序列化元数据，不持有 Viewer、场景或 UI 状态；
 * 3. 导出、导入、历史回放和 web-client 复制命令都通过该服务共享同一契约。
 */
export class AssemblyReferenceCopyMetadataService {
  /**
   * 写入引用复制元数据。
   *
   * @param object 目标 Object3D。
   * @param metadata 引用复制元数据。
   */
  public static writeMetadata(
    object: Object3D,
    metadata: AssemblyReferenceCopyMetadata,
  ): void {
    object.metadata = {
      ...(isRecord(object.metadata) ? object.metadata : {}),
      [ASSEMBLY_REFERENCE_COPY_METADATA_KEY]: cloneReferenceCopyMetadata(metadata),
    };
  }

  /**
   * 清理目标子树上的引用复制元数据。
   *
   * @param root 目标 Object3D 子树根。
   */
  public static clearMetadataInTree(root: Object3D): void {
    root.traverse((object) => {
      if (!isRecord(object.metadata) || !(ASSEMBLY_REFERENCE_COPY_METADATA_KEY in object.metadata)) {
        return;
      }

      const nextMetadata = { ...object.metadata };
      delete nextMetadata[ASSEMBLY_REFERENCE_COPY_METADATA_KEY];
      object.metadata = nextMetadata;
    });
  }

  /**
   * 读取对象自身的引用复制元数据。
   *
   * @param object 目标 Object3D。
   * @returns 引用复制元数据；不存在时返回 null。
   */
  public static readOwnMetadata(object: Object3D | null | undefined): AssemblyReferenceCopyMetadata | null {
    if (!object || !isRecord(object.metadata)) {
      return null;
    }

    return parseReferenceCopyMetadata(object.metadata[ASSEMBLY_REFERENCE_COPY_METADATA_KEY]);
  }

  /**
   * 从对象自身或父链读取引用复制元数据。
   *
   * @param object 起始 Object3D。
   * @returns 引用复制元数据；不存在时返回 null。
   */
  public static readMetadataFromSelfOrAncestors(
    object: Object3D | null | undefined,
  ): AssemblyReferenceCopyMetadata | null {
    let current = object ?? null;
    while (current) {
      const metadata = this.readOwnMetadata(current);
      if (metadata) {
        return metadata;
      }
      current = current.parent;
    }
    return null;
  }
}
